import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EnvironmentActions } from '@app/core/actions';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import * as fromRoot from '@app/reducers';
import { isValidStatusToStartUpload, shouldStartNewUpload } from '@app/shared/helpers/uploader';
import { Customization } from '@app/shared/models/customization';
import { TransferSettings } from '@app/shared/models/domainSettings';
import { Identity } from '@app/shared/models/identity';
import { Promotion } from '@app/shared/models/promotion';
import { Transfer } from '@app/shared/models/transfer';
import { DeliveryType } from '@app/shared/models/transferConfiguration';
import { DialogService } from '@ngneat/dialog';
import { Store, select } from '@ngrx/store';
import { GetEmailChallengeOutput } from '@smash-sdk-private/kyc/ejs/11-2023';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { UploaderStatus } from '@smash-sdk/uploader';
import moment from 'moment';
import { Observable, Subscription, combineLatest, lastValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, first, withLatestFrom } from 'rxjs/operators';
import { Constant } from 'src/constant';
import { TransferCommonParameters } from '@app/client/uploader/interfaces/transfer';
import { PrepareActions, UploadActions } from '@app/client/uploader/store/actions';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { ModalSizePremiumComponent } from '../modals/modal-size-premium/modal-size-premium.component';
import { EmailFormComponent } from './email-form/email-form.component';
import { EmailValidationComponent } from './email-validation/email-validation.component';
import { LinkFormComponent } from './link-form/link-form.component';
import { ResolveCustomizationService } from '@app/core/services/resolvers/resolve-customization';
import { ResolveExternalPromotionService } from '@app/core/services/resolvers/resolve-external-promotion';
import { removeTagFromEmail } from '@app/shared/helpers/validators';

@Component({
  selector: 'app-upload-parameters-box',
  templateUrl: './upload-parameters-box.component.html',
  styleUrls: ['./upload-parameters-box.component.scss']
})
export class UploadParametersBoxComponent implements OnInit, OnDestroy {

  @Input() dropbox?: string;
  @Input() uploadFormMode: DeliveryType;
  @Input() canConfigSmash: boolean;
  @Input() transferSettings: TransferSettings;

  @ViewChild('emailForm', { static: false }) emailForm: EmailFormComponent;
  @ViewChild('linkForm', { static: false }) linkForm: LinkFormComponent;
  @ViewChild('emailValidation') emailValidationComponent: EmailValidationComponent;

  identity$: Observable<Identity>;

  promotions: Promotion[];
  promotionSelected: Promotion;

  customization: Customization;
  customizationLoading: boolean;
  promotionLoading: boolean;
  transfer$: Observable<Transfer>;
  transferCommonParameters$: Observable<TransferCommonParameters>;
  transferPassword$: Observable<string>;
  transferAvailabilityDuration$: Observable<number>;

  transferQueue$: Observable<number>;
  transferQueueLoaded$: Observable<boolean>;
  transferQueueLoading$: Observable<boolean>;

  verifyEmailResponse$: Observable<GetEmailChallengeOutput['emailChallenge']['status']>;
  verifyEmailLoaded$: Observable<boolean>;
  verifyEmailLoading$: Observable<boolean>;
  verifyEmailError$: Observable<any>;

  validateEmailVerificationCodeResponse$: Observable<any>;
  validateEmailVerificationCodeLoaded$: Observable<boolean>;
  validateEmailVerificationCodeLoading$: Observable<boolean>;
  validateEmailVerificationCodeError$: Observable<any>;

  sendEmailVerificationLoading$: Observable<boolean>;
  sendEmailVerificationLoaded$: Observable<boolean>;
  sendEmailVerificationError$: Observable<any>;

  verificationCodeFormDisplayed$: Observable<boolean>;

  uploadStatus$: Observable<UploaderStatus>;
  updateAndResumeUploadLoading$: Observable<boolean>;
  drawerExpanded$: Observable<boolean>;
  transferSize$: Observable<any>;
  domain$: Observable<GetRecordOutput['record']>;
  selectedLanguage$: Observable<string>;
  countryCode$: Observable<string>;
  captchaChallenge$: Observable<string>;
  countries$: Observable<any[]>;
  transferErrorMessage$: Observable<string>;
  modalSizePremiumDisplayed$: Observable<boolean>;
  externalPromotionSelected$: Observable<Promotion>;

  transferSize: any;
  currentDomain: any;
  domain: string;
  configFormStatus: string;
  tabTranslate = {
    Email: 'UPLOADER_EMAIL_TAB_LABEL',
    Link: 'UPLOADER_LINK_TAB_LABEL',
  };
  translatedAvailabilityDuration: string;
  queue: number;
  queueLoaded = false;
  verifyEmailResponse = '';
  subscriptions: Subscription[] = [];

  constructor(
    private store$: Store<fromRoot.State & fromUploader.State>,
    private dialog: DialogService,
    private resolveCustomizationService: ResolveCustomizationService,
    private resolveExternalPromotionService: ResolveExternalPromotionService,
    private cd: ChangeDetectorRef,
    private gtmService: GoogleTagManagerService,
  ) {

    this.customizationLoading = true;
    this.promotionLoading = true;
    this.identity$ = this.store$.select(fromRoot.getActiveIdentity);
    this.transfer$ = this.store$.select(fromUploader.getTransfer);
    this.transferCommonParameters$ = this.store$.select(fromUploader.getTransferCommonParameters);
    this.transferPassword$ = this.store$.select(fromUploader.getTransferPassword);
    this.transferAvailabilityDuration$ = this.store$.select(fromUploader.getTransferAvailabilityDuration);
    this.transferQueue$ = this.store$.select(fromUploader.getTransferQueue);

    this.uploadStatus$ = this.store$.select(fromUploader.getUploadStatus);

    this.selectedLanguage$ = this.store$.select(fromRoot.getSelectedLanguage);
    this.drawerExpanded$ = this.store$.select(fromUploader.getConfigDrawerExpanded);
    this.transferSize$ = this.store$.select(fromUploader.getTransferSize);
    this.domain$ = this.store$.select(fromRoot.getCurrentDomain);
    this.countries$ = this.store$.select(fromRoot.getCountries);
    this.countryCode$ = this.store$.select(fromRoot.getCountryCode);
    this.captchaChallenge$ = this.store$.select(fromRoot.getCaptchaChallenge);
    this.modalSizePremiumDisplayed$ = this.store$.select(fromUploader.getModalSizePremiumDisplayed);

    this.verifyEmailResponse$ = this.store$.select(fromUploader.getVerifyEmailResponse);
    this.verifyEmailLoaded$ = this.store$.select(fromUploader.getVerifyEmailLoaded);
    this.verifyEmailLoading$ = this.store$.select(fromUploader.getVerifyEmailLoading);
    this.verifyEmailError$ = this.store$.select(fromUploader.getVerifyEmailError);

    this.validateEmailVerificationCodeResponse$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeResponse);
    this.validateEmailVerificationCodeLoaded$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeLoaded);
    this.validateEmailVerificationCodeLoading$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeLoading);
    this.validateEmailVerificationCodeError$ = this.store$.select(fromUploader.getValidateEmailVerificationCodeError);

    this.sendEmailVerificationLoaded$ = this.store$.select(fromUploader.getSendEmailVerificationLoaded);
    this.sendEmailVerificationLoading$ = this.store$.select(fromUploader.getSendEmailVerificationLoading);
    this.sendEmailVerificationError$ = this.store$.select(fromUploader.getSendEmailVerificationError);

    this.verificationCodeFormDisplayed$ = this.store$.select(fromUploader.getVerificationCodeFormDisplayed);

    this.updateAndResumeUploadLoading$ = this.store$.select(fromUploader.getUpdateAndResumeUploadLoading);
    this.externalPromotionSelected$ = this.store$.select(fromUploader.getExternalPromotionSelected);

    this.subscriptions.push(
      this.store$.select(fromUploader.getConfigFormStatus).subscribe(status => { this.configFormStatus = status; }),
      this.domain$.subscribe(domain => { this.currentDomain = domain; }),
      combineLatest([this.transferAvailabilityDuration$, this.selectedLanguage$]).subscribe(([availabilityDuration, language]) => {
        moment.locale(language);
        if (language === 'en') {
          this.translatedAvailabilityDuration = moment().add(availabilityDuration, 'seconds').format('ll');
        } else {
          this.translatedAvailabilityDuration = moment().add(availabilityDuration, 'seconds').format('L');
        }
      }),

      this.modalSizePremiumDisplayed$.pipe(distinctUntilChanged()).subscribe((displayed) => {
        if (displayed === true) {
          this.store$.dispatch(EnvironmentActions.loadCountries());
          this.store$.pipe(
            select(fromRoot.getCountriesLoaded),
            first(loaded => !!loaded),
            withLatestFrom(
              this.store$.select(fromRoot.getCountryCode),
              this.store$.select(fromRoot.getCountries),
            )).subscribe(() => {
              this.openModalSizePremium();
            });
        }
      }),
      this.verifyEmailResponse$.subscribe(response => {
        if (response === 'Dirty') {
          this.store$.dispatch(PrepareActions.DisplayVerificationCodeForm());
        }
      }),
      this.validateEmailVerificationCodeResponse$.subscribe(async response => {
        if (response === 'Clean') {
          await this.upload();
          this.store$.dispatch(PrepareActions.HideVerificationCodeForm());
        }
      }),
      this.externalPromotionSelected$.subscribe(promotion => this.promotionSelected = promotion)
    );
  }

  ngOnInit() {

    this.subscriptions.push(
      this.resolveCustomizationService.resolve().subscribe(({ customization }) => {
        this.customizationLoading = false;
        this.customization = customization;
        this.cd.markForCheck();
      }),
      this.resolveExternalPromotionService.resolve().subscribe(({ selectedPromotion, promotions }) => {
        this.promotionLoading = false;
        this.promotionSelected = selectedPromotion;
        this.promotions = promotions;
        this.cd.markForCheck();
      }),
    )
    if (!this.uploadFormMode && this.transferSettings?.delivery?.options?.length > 0) {
      this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode: this.transferSettings.delivery.options[0] as DeliveryType }));
    }
  }

  onSelectSmashMode(uploadFormMode: DeliveryType): void {
    this.store$.dispatch(PrepareActions.setUploadFormMode({ uploadFormMode }));
  }

  get emailInput() {
    return this.uploadFormMode === 'Email' ? this.emailForm.emailForm.value.sender.email : this.linkForm.linkForm.value.sender.email;
  }

  async upload() {
    // FIX ME Why do we need to set the transfer customization and promotion here?
    if (this.customization) {
      this.store$.dispatch(PrepareActions.SetTransferCustomization({ logo: this.customization.logo, background: this.customization.background }));
    }
    if (this.promotionSelected) {
      this.store$.dispatch(PrepareActions.SetTransferPromotion({ promotion: this.promotionSelected }));
    }

    const uploadStatus = await lastValueFrom(this.uploadStatus$.pipe(first()));
    if (!uploadStatus || shouldStartNewUpload(uploadStatus)) {
      this.store$.dispatch(UploadActions.Upload());
    } else {
      this.store$.dispatch(UploadActions.UpdateAndResumeUpload());
    }

    const transferSize = await lastValueFrom(this.transferSize$.pipe(first()));
    this.gtmService.pushTag({
      event: Constant.eventGTM.START_TRANSFER,
      transfer_size: transferSize,
    });
  }

  async verifyEmail() {
    this.store$.dispatch(PrepareActions.VerifyEmail({ username: this.emailInput }));
    const [verifyEmailResponse, verifyEmailError] = await lastValueFrom(this.store$.select(fromUploader.getVerifyEmailResponse)
      .pipe(
        withLatestFrom(this.verifyEmailError$, this.verifyEmailLoading$, this.verifyEmailLoaded$),
        filter(([_, verifyEmailError, verifyEmailLoading, verifyEmailLoaded]) => (verifyEmailLoaded && !verifyEmailLoading) || !!verifyEmailError),
        first(),
      ));

    if (verifyEmailError) {
      return false;
    }

    if (verifyEmailResponse === 'Clean') {
      this.store$.dispatch(PrepareActions.HideVerificationCodeForm());
      return true;
    }
    this.store$.dispatch(PrepareActions.DisplayVerificationCodeForm());
    return false;
  }

  async canByPassEmailVerification() {
    const identity = await lastValueFrom(this.identity$.pipe(first(identity => !!identity)));
    const emailInput = removeTagFromEmail(this.emailInput);
    return identity?.username && identity?.username === emailInput;
  }

  async isVerificationCodeValid() {
    const [validateEmailVerificationCodeResponse, validateEmailVerificationCodeError] = await lastValueFrom(this.validateEmailVerificationCodeResponse$
      .pipe(
        withLatestFrom(this.validateEmailVerificationCodeError$, this.validateEmailVerificationCodeLoading$, this.validateEmailVerificationCodeLoaded$),
        filter(([_, validateEmailVerificationCodeError, validateEmailVerificationCodeLoading, validateEmailVerificationCodeLoaded]) => (validateEmailVerificationCodeLoaded && !validateEmailVerificationCodeLoading) || !!validateEmailVerificationCodeError),
        first(),
      ));
    return validateEmailVerificationCodeResponse === 'Clean' && !validateEmailVerificationCodeError;
  }

  async canSubmitForm() {
    const uploadStatus = await lastValueFrom(this.uploadStatus$.pipe(first()));
    const isVerifyingEmail = await lastValueFrom(this.verifyEmailLoading$.pipe(first()));

    return (!uploadStatus || isValidStatusToStartUpload(uploadStatus)) && !isVerifyingEmail;
  }

  async handleFormSubmitted() {
    const canSubmitForm = await this.canSubmitForm();

    if (!canSubmitForm) {
      return;
    }

    const canByPassEmailVerification = await this.canByPassEmailVerification();

    if (canByPassEmailVerification) {
      await this.upload();
    } else {
      const isEmailVerified = await this.verifyEmail();
      if (isEmailVerified) {
        await this.upload();
      }
    }
  }

  async openModalSizePremium() {
    const countries = await this.countries$.pipe(first(countries => !!countries)).toPromise();
    const countryCode = await this.countryCode$.pipe(first(countryCode => !!countryCode)).toPromise();
    const queue = await this.transferQueue$.pipe(first(queue => !!queue)).toPromise();
    const transferSize = await this.transferSize$.pipe(first(size => !!size)).toPromise();
    this.dialog.open(ModalSizePremiumComponent,
      {
        closeButton: false,
        enableClose: true,
        size: 'sm',
        data: {
          countries,
          countryCode,
          queue,
          transferSize,
        },
      }
    );
  }

  submitVerificationCode() {
    this.emailValidationComponent.submit();
  }

  submitForm() {
    if (this.uploadFormMode) {
      switch (this.uploadFormMode) {
        case 'Email':
          this.emailForm.onSubmit();
          break;
        case 'Link':
          this.linkForm.onSubmit();
          break;
        default:
          this.emailForm.onSubmit();
          break;
      }
    }
  }

  handleOpenCloseDrawer(): void {
    if (this.canConfigSmash && this.configFormStatus !== 'INVALID') {
      this.store$.dispatch(PrepareActions.toggleConfigDrawerExpanded());
    }
  }

  handleCloseVerificationCode(): void {
    this.store$.dispatch(PrepareActions.HideVerificationCodeForm());
    this.store$.dispatch(PrepareActions.ResetVerifyEmail());
    this.emailValidationComponent.verificationCodeComponent.reset();
  }

  ngOnDestroy(): void {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }
}
